import React, { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "@/context/ThemeContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import LoginPage from "@/pages/LoginPage";
import AuthCallback from "@/pages/AuthCallback";
import ProfileCreation from "@/pages/ProfileCreation";
import HomePage from "@/pages/HomePage";
import VerificationPage from "@/pages/VerificationPage";
import SupportPanel from "@/pages/SupportPanel";
import AdminAuditPanel from "@/pages/AdminAuditPanel";
import TransactionHistory from "@/pages/TransactionHistory";
import PublicCertificatePage from "@/pages/PublicCertificatePage";
import MaintenancePage from "@/pages/MaintenancePage";
import { Toaster } from "sonner";

// PRODUCTION build: authentication uses YOUR Google OAuth (see backend /auth/google/*).
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ProtectedRoute({ children, requireProfile = false }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950" data-testid="loading-screen">
        <div className="animate-spin w-8 h-8 border-2 border-yellow-400 border-t-transparent rounded-full" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (requireProfile && !user.profile_complete) return <Navigate to="/profile-setup" replace />;
  return children;
}

function MaintenanceGate({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [maint, setMaint] = useState(null);

  useEffect(() => {
    const fetchMaint = () => axios.get(`${API}/maintenance`)
      .then(res => setMaint(res.data)).catch(() => setMaint({ enabled: false }));
    fetchMaint();
    const t = setInterval(fetchMaint, 30000);
    return () => clearInterval(t);
  }, []);

  const isPublicRoute = location.pathname.startsWith('/certificate/');
  if (maint?.enabled && !isPublicRoute && !loading && user && !user.is_admin) {
    return <MaintenancePage until={maint.until} message={maint.message} />;
  }
  return children;
}

function AppRouter() {
  return (
    <MaintenanceGate>
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/certificate/:id" element={<PublicCertificatePage />} />
      <Route path="/profile-setup" element={
        <ProtectedRoute><ProfileCreation /></ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute requireProfile={true}><HomePage /></ProtectedRoute>
      } />
      <Route path="/verification" element={
        <ProtectedRoute requireProfile={true}><VerificationPage /></ProtectedRoute>
      } />
      <Route path="/kyc" element={
        <ProtectedRoute requireProfile={true}><VerificationPage /></ProtectedRoute>
      } />
      <Route path="/support" element={
        <ProtectedRoute><SupportPanel /></ProtectedRoute>
      } />
      <Route path="/admin-audit" element={
        <ProtectedRoute><AdminAuditPanel /></ProtectedRoute>
      } />
      <Route path="/audit" element={
        <ProtectedRoute><AdminAuditPanel /></ProtectedRoute>
      } />
      <Route path="/transactions" element={
        <ProtectedRoute requireProfile={true}><TransactionHistory /></ProtectedRoute>
      } />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
    </MaintenanceGate>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster
            position="top-right"
            toastOptions={{
              style: { background: '#18181b', color: '#fafafa', border: '1px solid #27272a' }
            }}
          />
          <AppRouter />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
